import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, applicationsApi, interviewsApi } from '../services/api';
import OwnerLensToggle, { useStickyOwnerLens } from '../components/common/OwnerLensToggle';
import CommandCenterSummary from '../components/common/CommandCenterSummary';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import PlanUsageBanner from '../components/PlanUsageBanner';
import GoalBanner from '../components/GoalBanner';
import MissingCtcBanner from '../components/MissingCtcBanner';
import MissingCtcStageTile from '../components/MissingCtcStageTile';
import CtcOutlierBanner from '../components/CtcOutlierBanner';
import ClosureHealthTile from '../components/ClosureHealthTile';
import ClosureHealthReport from '../components/ClosureHealthReport';
import OfferVsJoiningCard from '../components/analytics/OfferVsJoiningCard';
import JoiningThisWeekTile from '../components/JoiningThisWeekTile';
import TopClosuresWidget from '../components/TopClosuresWidget';
import { useCanSeeRevenue } from '../lib/perms';
import {
  Briefcase,
  Users,
  Calendar,
  Building2,
  TrendingUp,
  TrendingDown,
  Clock,
  ArrowRight,
  UserCheck,
  FileText,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const stageBadgeClass = {
  sourced: 'badge-sourced',
  screening: 'badge-screening',
  client_submission: 'badge-submission',
  interview_scheduled: 'badge-interview',
  interview_selected: 'badge-selected',
  offer_released: 'badge-offer',
  joined: 'badge-joined',
  rejected: 'badge-rejected',
  dropped: 'badge-dropped',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ['admin', 'delivery_manager', 'team_lead'].includes(user?.role);
  const canSeeRevenue = useCanSeeRevenue();
  const [stats, setStats] = useState(null);
  const [recentApps, setRecentApps] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sticky ownership lens — same storage key as Command Center & Pipeline,
  // so a manager who already chose 'Owned' or 'Assigned' there sees their
  // dashboard reflect that scope without having to re-toggle.
  const [ownerLens, setOwnerLens] = useStickyOwnerLens('owner_lens', 'created');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerLens]);

  // Refresh dashboard tiles when CTC is backfilled via MissingCtcBanner.
  useEffect(() => {
    const onBackfill = () => fetchData();
    window.addEventListener('ctc:backfilled', onBackfill);
    return () => window.removeEventListener('ctc:backfilled', onBackfill);
  }, []);

  const fetchData = async () => {
    try {
      const lensParams = isManager ? { owner_lens: ownerLens } : {};
      const [statsRes, activityRes, interviewsRes] = await Promise.all([
        dashboardApi.getStats(lensParams),
        dashboardApi.getRecentActivity(lensParams),
        interviewsApi.getAll(),
      ]);
      setStats(statsRes.data);
      setRecentApps(activityRes.data.recent_applications || []);
      
      // Handle paginated response for interviews
      const interviewsData = interviewsRes.data;
      const allInterviews = interviewsData.items || (Array.isArray(interviewsData) ? interviewsData : []);
      
      // Filter upcoming interviews
      const now = new Date();
      const upcoming = allInterviews
        .filter(i => new Date(i.scheduled_at) > now)
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
        .slice(0, 5);
      setUpcomingInterviews(upcoming);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Each stat card carries `deltaKey` matching the backend's `deltas_vs_yesterday`
  // map. Chip hidden when delta is null/0 (per user spec — no noise).
  const statCards = [
    { label: 'Active Jobs', value: stats?.active_jobs || 0, deltaKey: 'active_jobs', icon: Briefcase, color: 'bg-teal-100 text-teal-700' },
    { label: 'Total Candidates', value: stats?.total_candidates || 0, deltaKey: 'total_candidates', icon: Users, color: 'bg-blue-100 text-blue-700' },
    { label: 'Applications', value: stats?.total_applications || 0, deltaKey: 'total_applications', icon: FileText, color: 'bg-purple-100 text-purple-700' },
    { label: 'Upcoming Interviews', value: stats?.upcoming_interviews || 0, deltaKey: 'upcoming_interviews', icon: Calendar, color: 'bg-amber-100 text-amber-700' },
    { label: 'Total Clients', value: stats?.total_clients || 0, deltaKey: 'total_clients', icon: Building2, color: 'bg-emerald-100 text-emerald-700' },
    { label: 'Joined', value: stats?.pipeline_stats?.joined || 0, deltaKey: 'joined', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Welcome back! Here's your hiring overview.</p>
        </div>
        {/* Owner lens — manager-only. Sticky across Dashboard ↔ Command
            Center ↔ Pipeline via the shared `owner_lens` storage key, so a
            manager who's investigating "what was handed to my team" on the
            kanban sees the same scope when they jump back to the Dashboard. */}
        {isManager ? (
          <OwnerLensToggle
            value={ownerLens}
            onChange={setOwnerLens}
            visible={isManager}
            testid="dashboard-owner-lens"
          />
        ) : null}
      </div>

      {/* Plan usage banner — hidden for enterprise / dismissed */}
      <PlanUsageBanner />

      {/* Recruiter monthly goal nudge — hidden for non-recruiter roles */}
      <GoalBanner />

      {/* Missing-CTC alert — hidden when count = 0 or dismissed for 12h */}
      <MissingCtcBanner />

      {/* Compact stage-split tile (Offered + Joined) — click-through to CtcCleanup */}
      <MissingCtcStageTile />

      {/* Out-of-range CTC warning — admin only, hidden when count = 0 or dismissed today */}
      <CtcOutlierBanner />

      {/* Joining this week — managers only, hidden when empty */}
      <JoiningThisWeekTile />

      {/* Closure Health Report & Offer→Joining funnel — admin-only because
          they surface revenue / CTC totals. Non-admin roles never see ₹. */}
      {canSeeRevenue && (
        <>
          <TopClosuresWidget />
          <ClosureHealthReport />
          <OfferVsJoiningCard
            variant="default"
            showPeriodSelector
            testid="dashboard-ovj"
          />
        </>
      )}

      {/* Recruiter Command Center summary — moved from /command-center.
          Mounted ABOVE the Quick Stats so users land into productivity
          KPIs first and then drill into the volume/funnel tiles below. */}
      <CommandCenterSummary />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="stats-grid">
        {statCards.map((stat, index) => {
          const delta = stats?.deltas_vs_yesterday?.[stat.deltaKey];
          const showChip = typeof delta === 'number' && delta !== 0;
          const positive = (delta || 0) > 0;
          return (
            <Card key={index} className="card-hover" data-testid={`stat-${stat.deltaKey}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  {showChip && (
                    <span
                      className={`inline-flex items-center gap-0.5 text-[10px] font-bold rounded px-1.5 py-0.5 ${
                        positive
                          ? 'text-emerald-700 bg-emerald-50'
                          : 'text-rose-700 bg-rose-50'
                      }`}
                      title={`${positive ? '+' : ''}${delta} vs yesterday`}
                      data-testid={`stat-${stat.deltaKey}-delta`}
                    >
                      {positive ? (
                        <TrendingUp className="w-2.5 h-2.5" />
                      ) : (
                        <TrendingDown className="w-2.5 h-2.5" />
                      )}
                      {positive ? '+' : ''}{delta}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </CardContent>
            </Card>
          );
        })}
        {/* Closure Health — % of Joined apps with verified CTC. Hides
            automatically when there are no closures yet. Admin-only since
            it surfaces revenue health. */}
        {canSeeRevenue && <ClosureHealthTile />}
      </div>

      {/* Pipeline Overview */}
      <Card data-testid="pipeline-overview">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Pipeline Overview</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/pipeline')} data-testid="view-pipeline-btn">
            View All <ArrowRight className="ml-1 w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats?.pipeline_stats || {}).map(([stage, count]) => (
              <div
                key={stage}
                className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg"
              >
                <Badge className={stageBadgeClass[stage] || 'bg-slate-100'}>
                  {stage.replace('_', ' ')}
                </Badge>
                <span className="font-semibold text-slate-900">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Applications */}
        <Card data-testid="recent-applications">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Applications</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/pipeline')} data-testid="view-all-apps-btn">
              View All <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentApps.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No recent applications</p>
            ) : (
              <div className="space-y-3">
                {recentApps.slice(0, 5).map((app) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate('/pipeline')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-teal-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{app.candidate_name}</p>
                        <p className="text-sm text-slate-500">{app.job_title}</p>
                      </div>
                    </div>
                    <Badge className={stageBadgeClass[app.stage] || 'bg-slate-100'}>
                      {app.stage.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Interviews */}
        <Card data-testid="upcoming-interviews">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Upcoming Interviews</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/interviews')} data-testid="view-all-interviews-btn">
              View All <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingInterviews.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No upcoming interviews</p>
            ) : (
              <div className="space-y-3">
                {upcomingInterviews.map((interview) => (
                  <div
                    key={interview.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate('/interviews')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-amber-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{interview.candidate_name}</p>
                        <p className="text-sm text-slate-500">{interview.job_title}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">
                        {format(new Date(interview.scheduled_at), 'MMM dd')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {format(new Date(interview.scheduled_at), 'hh:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
